/**
 * Rhythmz — ModelSettings
 * ───────────────────────
 * Persisted settings for AI model selection.
 * Uses localStorage for persistence.
 *
 * Default: Workers AI (free, no keys required).
 * Supports: Workers AI, Groq, Gemini, OpenAI, Anthropic, Custom endpoints.
 */

export type ProviderType = 'workers-ai' | 'groq' | 'gemini' | 'openai' | 'anthropic' | 'custom';

export interface AIModelSettings {
  /** The selected model ID from the registry */
  modelId: string;
  /** Provider type */
  provider: ProviderType;
  /** For custom endpoints — full URL to the OpenAI-compatible API */
  customEndpoint: string;
  /** The model name to use with the custom endpoint */
  customModelName: string;
  /** API keys (stored in localStorage — caution) */
  apiKeys: {
    groq?: string;
    gemini?: string;
    openai?: string;
    anthropic?: string;
  };
  /** Whether to use the local WebLLM model instead of edge */
  useLocalModel: boolean;
  /** Local WebLLM model ID (e.g., 'Phi-4-mini-instruct-q4f16_1-MLC') */
  localModelId: string;
  /** Auto-generated: date of last settings change */
  lastUpdated: string;
}

const STORAGE_KEY = 'rhythmz-ai-settings';

const DEFAULT_SETTINGS: AIModelSettings = {
  modelId: 'workers-llama-3.2-3b',
  provider: 'workers-ai',
  customEndpoint: '',
  customModelName: '',
  apiKeys: {},
  useLocalModel: false,
  localModelId: 'Phi-4-mini-instruct-q4f16_1-MLC',
  lastUpdated: new Date().toISOString(),
};

export class ModelSettings {
  private settings: AIModelSettings;

  constructor() {
    this.settings = this.load();
  }

  /** Load settings from localStorage. */
  private load(): AIModelSettings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch { /* ignore corrupt storage */ }
    return { ...DEFAULT_SETTINGS };
  }

  /** Save current settings to localStorage. */
  save(): void {
    this.settings.lastUpdated = new Date().toISOString();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch { /* storage full */ }
  }

  /** Get the full settings object. */
  getAll(): AIModelSettings {
    return { ...this.settings };
  }

  /** Update settings (partial merge). */
  update(partial: Partial<AIModelSettings>): void {
    this.settings = { ...this.settings, ...partial };
    this.save();
  }

  /** Get the current provider type. */
  getProvider(): ProviderType {
    return this.settings.provider;
  }

  /** Get the current model ID. */
  getModelId(): string {
    return this.settings.modelId;
  }

  /** Whether the user has configured keys for premium providers. */
  hasPremiumAccess(): boolean {
    return !!(
      this.settings.apiKeys.openai ||
      this.settings.apiKeys.anthropic ||
      this.settings.apiKeys.groq ||
      this.settings.apiKeys.gemini
    );
  }

  /** Whether a specific provider has an API key configured. */
  hasKeyFor(provider: ProviderType): boolean {
    return !!this.settings.apiKeys[provider as keyof typeof this.settings.apiKeys];
  }

  /** Get the API key for a provider. */
  getKey(provider: ProviderType): string | undefined {
    return this.settings.apiKeys[provider as keyof typeof this.settings.apiKeys];
  }

  /** Set an API key for a provider. */
  setKey(provider: ProviderType, key: string): void {
    this.settings.apiKeys[provider as keyof typeof this.settings.apiKeys] = key;
    this.save();
  }

  /** Remove an API key. */
  removeKey(provider: ProviderType): void {
    delete this.settings.apiKeys[provider as keyof typeof this.settings.apiKeys];
    this.save();
  }

  /** Get the custom endpoint URL. */
  getCustomEndpoint(): string {
    return this.settings.customEndpoint;
  }

  /** Get the custom model name. */
  getCustomModelName(): string {
    return this.settings.customModelName;
  }

  /** Whether to use the local WebLLM model. */
  getUseLocalModel(): boolean {
    return this.settings.useLocalModel;
  }

  /** Set local model preference. */
  setUseLocalModel(val: boolean): void {
    this.settings.useLocalModel = val;
    this.save();
  }

  /** Get the local WebLLM model ID. */
  getLocalModelId(): string {
    return this.settings.localModelId;
  }

  /** Reset to defaults. */
  reset(): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.save();
  }

  /** Build the API endpoint payload for edge requests. */
  getRequestPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      modelId: this.settings.modelId,
      provider: this.settings.provider,
    };

    if (this.settings.provider === 'custom') {
      payload.endpoint = this.settings.customEndpoint;
      payload.model = this.settings.customModelName;
    }

    const key = this.settings.apiKeys[this.settings.provider as keyof typeof this.settings.apiKeys];
    if (key) {
      payload.apiKey = key;
    }

    return payload;
  }

  /** Human-readable label for the current provider. */
  getProviderLabel(): string {
    const labels: Record<ProviderType, string> = {
      'workers-ai': 'Cloudflare Workers AI (free)',
      groq: 'Groq (free tier)',
      gemini: 'Google Gemini (free tier)',
      openai: 'OpenAI',
      anthropic: 'Anthropic',
      custom: 'Custom Endpoint',
    };
    return labels[this.settings.provider] ?? this.settings.provider;
  }

  /** Get a list of available edge providers (based on configured keys). */
  getAvailableEdgeProviders(): { id: ProviderType; label: string; configured: boolean }[] {
    return [
      { id: 'workers-ai', label: 'Workers AI (free)', configured: true },
      { id: 'groq', label: 'Groq AI', configured: !!this.settings.apiKeys.groq },
      { id: 'gemini', label: 'Google Gemini', configured: !!this.settings.apiKeys.gemini },
      { id: 'openai', label: 'OpenAI', configured: !!this.settings.apiKeys.openai },
      { id: 'anthropic', label: 'Anthropic', configured: !!this.settings.apiKeys.anthropic },
      { id: 'custom', label: 'Custom Endpoint', configured: !!this.settings.customEndpoint },
    ];
  }
}

/** Singleton instance. */
export const modelSettings = new ModelSettings();